import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllLoanApplications, getApplicationDetails } from "../../API/API";
import { setFiles, setFormData, setResumed } from "../../ReduxStore/UserSlice";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: 0,
  margin: 0,
  display: "flex",
  justifyContent: "center",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  width: "100%",
  maxWidth: "1200px",
  padding: "24px 32px",
  boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.05)",
  border: "1px solid #E5E8EB",
  margin: "24px 16px",
  borderRadius: "10px",
});

const PageTitle = styled(Typography)({
  color: "#0C53B7",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "100%",
  letterSpacing: "0%",
  marginBottom: "8px",
});

const PageDescription = styled(Typography)({
  color: "#637381",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  fontStyle: "italic",
  lineHeight: "100%",
  letterSpacing: "0%",
  marginBottom: "12px",
});

const Divider = styled(Box)({
  width: "100%",
  height: "1px",
  backgroundColor: "#E5E8EB",
  margin: "12px 0 24px 0",
});

const ApplicationCard = styled(Box)({
  border: "1px solid #000000",
  borderRadius: "10px",
  padding: "24px 32px",
  marginBottom: "16px",
  position: "relative",
});

const ApplicationBadge = styled(Box)({
  position: "absolute",
  top: "20px",
  left: "20px",
  backgroundColor: "#0052CC",
  color: "white",
  borderRadius: "16px",
  padding: "4px 12px",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "80px",
  textAlign: "center",
});

const ApplicationDetails = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "36px",
});

const DetailGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const DetailLabel = styled(Typography)({
  color: "#637381",
  fontSize: "14px",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 400,
  marginBottom: "8px",
});

const DetailValue = styled(Typography)({
  color: "#212B36",
  fontSize: "16px",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 600,
  lineHeight: "150%",
});

const StatusBadge = styled(Box)(({ status }) => ({
  backgroundColor:
    status === "Incomplete"
      ? "rgba(255, 171, 0, 0.08)"
      : "rgba(24, 203, 150, 0.08)",
  color: status === "Incomplete" ? "#FFAB00" : "#18CB96",
  borderRadius: "16px",
  padding: "4px 12px",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  display: "inline-block",
}));

const ContinueButton = styled(Button)({
  backgroundColor: "#0052CC",
  color: "white",
  borderRadius: "8px",
  padding: "10px 24px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  height: "48px",
  minWidth: "210px",
  "&:hover": {
    backgroundColor: "#003E99",
  },
});

const EmptyState = ({ navigate }) => (
  <Box
    sx={{
      textAlign: "center",
      py: 8,
      px: 2,
      borderRadius: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
    }}
  >
    <img
      src="/No_Loan_Application.png"
      alt="No applications"
      style={{
        width: "180px",
        height: "180px",
        marginBottom: "24px",
      }}
    />
    <Typography
      variant="h6"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        fontSize: "24px",
        color: "#212B36",
        mb: 1,
        letterSpacing: "-0.02em",
      }}
    >
      No incomplete applications
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "14px",
        lineHeight: "24px",
        color: "#637381",
        maxWidth: "400px",
        mb: 3,
        textAlign: "center",
      }}
    >
      You don't have any incomplete loan applications. Start a new application
      from the Apply Loan section.
    </Typography>
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      <Button
        variant="contained"
        onClick={() =>
          navigate("/apply-loan", { state: { directNavigation: true } })
        }
        sx={{
          backgroundColor: "#0052CC",
          "&:hover": {
            backgroundColor: "#0047B3",
          },
          color: "white",
          textTransform: "none",
          borderRadius: "8px",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          fontSize: "15px",
          padding: "10px 24px",
          height: "48px",
          boxShadow: "none",
        }}
      >
        Apply for Loan
      </Button>
    </Box>
  </Box>
);

const ResumeApplication = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // const applications = [
  //     {
  //         id: '1',
  //         number: 'Application 1',
  //         appliedOn: '15th Feb 2025',
  //         loanType: 'Business Loan',
  //         amount: '₹10,00,000',
  //         status: 'Incomplete'
  //     },
  //     {
  //         id: '2',
  //         number: 'Application 2',
  //         appliedOn: '05th March 2025',
  //         loanType: 'Home Loan',
  //         amount: '₹25,00,000',
  //         status: 'Incomplete'
  //     }
  // ];

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userFromRedux = useSelector((state) => state.user.user);
  const apiCallMade = useRef(false);

  useEffect(() => {
    if (apiCallMade.current) return;

    const fetchApplications = async () => {
      try {
        apiCallMade.current = true;
        setLoading(true);

        const userId =
          userFromRedux?.ID ||
          userFromRedux?.id ||
          (localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))?.ID
            : null);

        const response = await getAllLoanApplications(
          "resumeApplication",
          userId
        );

        if (
          response &&
          response.result &&
          response.result.data &&
          response.result.data.length > 0
        ) {
          setApplications(response.result.data);
        } else {
          setApplications([]);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Failed to load applications. Please try again later.");
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [userFromRedux]);

  const hasApplications = applications && applications.length > 0;

  const handleContinueApplication = async (applicationId) => {
    try {
      setLoading(true);
      const response = await getApplicationDetails(applicationId);
      if (response && response.result) {
        const applicationData = response.result;
        const sanitizedData = {};

        Object.entries(applicationData).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            sanitizedData[key] = "";
            return;
          }

          if (typeof value === "object" && !Array.isArray(value)) {
            if (key === "userId" || key === "documents") {
              sanitizedData[key] = value;
            } else {
              sanitizedData[key] = JSON.stringify(value);
            }
          } else if (Array.isArray(value)) {
            sanitizedData[key] = value;
          } else if (typeof value === "number") {
            sanitizedData[key] = value;
          } else if (typeof value === "boolean") {
            sanitizedData[key] = value;
          } else {
            sanitizedData[key] = String(value);
          }
        });

        sanitizedData.applicationId = applicationId;

        dispatch(setFormData(sanitizedData));

        if (applicationData.documents && applicationData.documents.length > 0) {
          const filesObj = {};

          const documentTypeMapping = {
            BorrowerPanCard: "borrowerPanCard",
            PartnershipDeed: "partnershipDeed",
            ListOfPatner: "listOfPatner",
            ITR: "itr",
            BalanceSheet: "balanceSheet",
            AadharCard: "AadharCard",
            PanCard: "panCard",
            BankStatement: "bankStatement",
            SalarySlips: "salarySlips",
            SLLoan: "slLoan",
            CompanyPAN: "borrowerPanCard",
            MOA: "moa",
            AOA: "aoa",
            ListOfDirector: "listOfDirector",
            Shareholding: "shareholding",
            CoApplicantITR: "coApplicantItr",
            ApplicantITR: "applicantItr",
            ApplicantPan: "applicantPan",
            ApplicantAadhar: "applicantAadhar",
            CoApplicantPan: "coApplicantPan",
            CoApplicantAadhar: "coApplicantAadhar",
            ApplicantForm16: "applicantForm16",
            CoApplicantForm16: "coApplicantForm16",
            GSTDocx: "gstDocx",
            AS26: "as26",
            "PAN Partner": "panCard",
            "Aadhar Partner": "aadharCard",
            "PAN Director": "panCard",
            "Aadhar Director": "AadharCard",
            "Aadhar Propreitor": "AadharCard",
            "PAN Propreitor": "panCard",
            OrderCopy: "orderCopy",
            ContractCopy: "contractCopy",
            Certification: "certification",
            CreditRating: "creditRating",
            JoiningLetter: "joiningLetter",
            IncorporationDateProof: "incorporationDateProof",
          };

          const sortedDocuments = [...applicationData.documents].sort(
            (a, b) => b.documentId - a.documentId
          );
          const documentsByType = {};

          sortedDocuments.forEach((doc) => {
            const docType = doc.documentType;
            if (!documentsByType[docType]) {
              documentsByType[docType] = doc;
            }
          });

          const arrayDocumentTypes = [
            "ITR",
            "BalanceSheet",
            "SalarySlips",
            "BankStatement",
            "SLLoan",
            "PanCard",
            "AadharCard",
          ];

          const partnerDocumentTypes = new Set();
          sortedDocuments.forEach((doc) => {
            if (
              doc.documentType.includes("Partner") &&
              doc.documentName &&
              doc.documentName.match(/-\d+$/)
            ) {
              const baseType = doc.documentType.split("-")[0].trim();
              partnerDocumentTypes.add(baseType);
            }
          });

          const directorDocumentTypes = new Set();
          sortedDocuments.forEach((doc) => {
            if (
              doc.documentType.includes("Director") &&
              doc.documentName &&
              doc.documentName.match(/-\d+$/)
            ) {
              const baseType = doc.documentType.split("-")[0].trim();
              directorDocumentTypes.add(baseType);
            }
          });

          const proprietorDocumentTypes = new Set();
          sortedDocuments.forEach((doc) => {
            if (
              (doc.documentType.includes("Propreitor") ||
                doc.documentType.includes("Proprietor")) &&
              doc.documentName &&
              doc.documentName.match(/-\d+$/)
            ) {
              const baseType = doc.documentType.split("-")[0].trim();
              proprietorDocumentTypes.add(baseType);
            }
          });

          partnerDocumentTypes.forEach((type) => {
            if (!arrayDocumentTypes.includes(type)) {
              arrayDocumentTypes.push(type);
            }
          });

          directorDocumentTypes.forEach((type) => {
            if (!arrayDocumentTypes.includes(type)) {
              arrayDocumentTypes.push(type);
            }
          });

          proprietorDocumentTypes.forEach((type) => {
            if (!arrayDocumentTypes.includes(type)) {
              arrayDocumentTypes.push(type);
            }
          });

          const arrayDocuments = {};

          sortedDocuments.forEach((doc) => {
            const docType = doc.documentType;
            if (arrayDocumentTypes.includes(docType)) {
              if (!arrayDocuments[docType]) {
                arrayDocuments[docType] = [];
              }

              let index = 0;
              if (doc.documentName) {
                const match = doc.documentName.match(/\d+$/);
                if (match) {
                  index = parseInt(match[0]) - 1;
                }
              }

              arrayDocuments[docType][index] = doc;
            } else if (
              doc.documentName &&
              doc.documentName.match(/-\d+$/) &&
              (doc.documentType.includes("Partner") ||
                doc.documentType.includes("Director"))
            ) {
              const baseType = doc.documentType; // Use the full type

              if (!arrayDocuments[baseType]) {
                arrayDocuments[baseType] = [];
              }

              const match = doc.documentName.match(/-(\d+)$/);
              if (match) {
                const index = parseInt(match[1]) - 1;
                arrayDocuments[baseType][index] = doc;
              } else {
                arrayDocuments[baseType].push(doc);
              }
            } else if (
              doc.documentName &&
              doc.documentName.match(/-\d+$/) &&
              (doc.documentType.includes("Propreitor") ||
                doc.documentType.includes("Proprietor"))
            ) {
              const baseType = doc.documentType; // Use the full type

              if (!arrayDocuments[baseType]) {
                arrayDocuments[baseType] = [];
              }

              const match = doc.documentName.match(/-(\d+)$/);
              if (match) {
                const index = parseInt(match[1]) - 1;
                arrayDocuments[baseType][index] = doc;
              } else {
                arrayDocuments[baseType].push(doc);
              }
            }
          });

          Object.entries(documentsByType).forEach(([docType, doc]) => {
            if (arrayDocumentTypes.includes(docType)) {
              return;
            }

            const fieldName =
              documentTypeMapping[docType] || docType.toLowerCase();

            filesObj[fieldName] = {
              filename: doc.documentName || docType,
              url: doc.documentPath,
              documentPath: doc.documentPath,
              remote: true,
              documentId: doc.documentId,
            };
          });

          Object.entries(arrayDocuments).forEach(([docType, docs]) => {
            const fieldName =
              documentTypeMapping[docType] || docType.toLowerCase();

            if (!fieldName) {
              return;
            }

            if (docs.length > 0) {
              filesObj[fieldName] = docs
                .map((doc) =>
                  doc
                    ? {
                        filename: doc.documentName || `${docType}`,
                        url: doc.documentPath,
                        documentPath: doc.documentPath,
                        documentId: doc.documentId,
                        remote: true,
                      }
                    : null
                )
                .filter(Boolean);
            }
          });

          const companyDocTypes = [
            "MOA",
            "AOA",
            "ListOfDirector",
            "Shareholding",
            "BorrowerPanCard",
          ];
          companyDocTypes.forEach((docType) => {
            const found = sortedDocuments.filter(
              (doc) => doc.documentType === docType
            );
            if (found.length > 0) {
            }
          });
          dispatch(setFiles(filesObj));
        }

        dispatch(setResumed(true));

        navigate(`/apply-loan?resume=${applicationId}`, {
          state: { fromResumePage: true },
        });
      } else {
        setError("Failed to fetch application details.");
      }
    } catch (err) {
      console.error("Error fetching application details:", err);
      setError("Failed to fetch application details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <ContentContainer
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
        </ContentContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentContainer>
        <PageTitle>Resume Application</PageTitle>
        <PageDescription>Continue from where you left off!</PageDescription>

        <Divider />

        {error && (
          <Typography
            sx={{
              color: "error.main",
              mb: 2,
              fontFamily: "'Inter', sans-serif",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {error}
          </Typography>
        )}

        {hasApplications ? (
          <>
            {applications.map((application) => (
              <ApplicationCard key={application.uuid}>
                <ApplicationBadge>
                  {application.applicationNumber ||
                    `Application ${application.applicationId}`}
                </ApplicationBadge>

                <ApplicationDetails>
                  <DetailGroup>
                    <DetailLabel>Applied On:</DetailLabel>
                    <DetailValue>
                      {new Date(
                        application.createDatetime
                      ).toLocaleDateString()}
                    </DetailValue>
                  </DetailGroup>

                  <DetailGroup>
                    <DetailLabel>Loan Type:</DetailLabel>
                    <DetailValue>
                      {application.borrowerType
                        ? application.borrowerType.replace("_", " ")
                        : "Not specified"}
                    </DetailValue>
                  </DetailGroup>

                  <DetailGroup>
                    <DetailLabel>Amount:</DetailLabel>
                    <DetailValue>
                      {application.fundRequirement
                        ? `₹${application.fundRequirement.toLocaleString()}`
                        : "Not specified"}
                    </DetailValue>
                  </DetailGroup>

                  <DetailGroup>
                    <DetailLabel>Status:</DetailLabel>
                    <StatusBadge
                      status={
                        application.status === "incomplete"
                          ? "Incomplete"
                          : application.status
                      }
                    >
                      {application.status === "incomplete"
                        ? "Incomplete"
                        : application.status}
                    </StatusBadge>
                  </DetailGroup>

                  <ContinueButton
                    variant="contained"
                    onClick={() =>
                      handleContinueApplication(application.applicationId)
                    }
                  >
                    Continue Application
                  </ContinueButton>
                </ApplicationDetails>
              </ApplicationCard>
            ))}
          </>
        ) : (
          <EmptyState navigate={navigate} />
        )}
      </ContentContainer>
    </PageWrapper>
  );
};

export default ResumeApplication;
